<script setup lang="ts">
  import { computed, ref, type PropType } from 'vue'
  import { ClockIcon, XIcon, Pencil } from 'lucide-vue-next'
  import { Button } from '@/components/ui/button'
  import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
  import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
  import {
    type CustomerEntityChange as EntityChange,
    type CustomerCaseDataChange,
    ChangeSourceEnum,
    AugmentationVariantEnum
  } from '@/types/generated'

  const props = defineProps({
    changes: {
      type: Object as PropType<EntityChange[] | CustomerCaseDataChange[]>,
      required: true
    },
    latestChange: {
      type: Object as PropType<EntityChange | CustomerCaseDataChange | null>,
      required: false,
      default: null
    },
    showTimeTravel: {
      type: Boolean,
      required: true
    }
  })

  const emit = defineEmits(['onVersionSelect'])

  const isPopoverOpen = ref(false)
  const expandedChanges = ref(new Set<string>())

  const toggleExpand = (uuid: string) => {
    const newSet = new Set(expandedChanges.value)
    if (newSet.has(uuid)) {
      newSet.delete(uuid)
    } else {
      newSet.add(uuid)
    }
    expandedChanges.value = newSet
  }

  const isTextTooLong = (text?: string) => {
    return text && text.length > 50
  }

  const isExpanded = (uuid: string) => {
    return expandedChanges.value.has(uuid)
  }

  const orderedChanges = computed(() => {
    return [...props.changes].sort((a, b) => {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    })
  })

  const selectedVersion = ref<string>(orderedChanges.value[0]?.uuid)

  const handleVersionSelect = async (uuid: string) => {
    const selectedChange = orderedChanges.value.find((change) => change.uuid === uuid)
    if (selectedChange && selectedChange.uuid !== orderedChanges.value[0]?.uuid) {
      // Emit value to parent component to handle change
      emit('onVersionSelect', selectedChange.new_value)
    } else {
      selectedVersion.value = uuid
    }
  }
</script>

<template>
  <div class="flex items-center">
    <span
      v-if="latestChange"
      class="text-sm text-muted-foreground"
    >
      {{
        new Date(latestChange.created_at).toLocaleDateString('de-DE', {
          day: '2-digit',
          month: '2-digit',
          year: '2-digit'
        })
      }}
    </span>
    <Popover v-model:open="isPopoverOpen">
      <PopoverTrigger>
        <Button
          v-if="latestChange && showTimeTravel"
          variant="ghost"
          size="icon"
        >
          <div class="relative">
            <span
              class="absolute -bottom-[5px] -left-[5px] text-[8px] leading-none text-muted-foreground"
            >
              {{ changes.length }}
            </span>
            <ClockIcon class="h-4 w-4" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent class="w-[480px]">
        <div>
          <div
            class="sticky top-0 z-10 flex items-center justify-between border-b bg-background px-3 py-2"
          >
            <h4 class="text-sm font-medium leading-none">Versionverlauf</h4>
            <Button
              variant="ghost"
              size="icon"
              class="h-4 w-4"
              @click="isPopoverOpen = false"
            >
              <XIcon class="h-4 w-4" />
            </Button>
          </div>
          <div class="max-h-[300px] overflow-y-auto">
            <RadioGroup
              v-model="selectedVersion"
              :default-value="orderedChanges[0]?.uuid"
              @update:model-value="handleVersionSelect"
            >
              <div class="space-y-0.5 p-1">
                <div
                  v-for="change in orderedChanges"
                  :key="change.uuid"
                  class="group relative grid cursor-pointer grid-cols-[1fr_120px_80px] items-center gap-3 rounded-md border
                    px-3 py-2 hover:bg-muted/50"
                  :class="{
                    'border-[#7C3AED] bg-[#7C3AED]/5': selectedVersion === change.uuid
                  }"
                  @click="handleVersionSelect(change.uuid)"
                >
                  <div class="absolute left-2">
                    <RadioGroupItem
                      :value="change.uuid"
                      class="border-[#7C3AED] text-[#7C3AED]"
                    />
                  </div>
                  <div class="min-w-0 pl-5">
                    <div class="flex min-w-0 flex-col gap-0.5">
                      <div
                        class="text-sm"
                        :class="{
                          'line-clamp-1':
                            !isExpanded(change.uuid) && isTextTooLong(change.new_value)
                        }"
                      >
                        {{ change.new_value }}
                      </div>
                      <button
                        v-if="isTextTooLong(change.new_value)"
                        class="text-xs text-[#7C3AED] hover:underline"
                        @click.stop="toggleExpand(change.uuid)"
                      >
                        {{ isExpanded(change.uuid) ? 'Show less' : 'Show more' }}
                      </button>
                    </div>
                  </div>
                  <div class="flex items-center justify-start">
                    <div
                      v-if="change.change_source == ChangeSourceEnum.AI"
                      class="flex items-center gap-1 whitespace-nowrap text-[#7C3AED]"
                    >
                      <div
                        class="flex h-4 w-4 items-center justify-center rounded bg-[#7C3AED]/10 text-[10px] font-medium
                          text-[#7C3AED]"
                      >
                        KI
                      </div>
                      <span
                        v-if="
                          change.ai_augmentation.augmentation_variant ===
                          AugmentationVariantEnum.DOCUMENT_ANALYSIS
                        "
                        class="text-xs font-medium"
                      >
                        Dokument
                      </span>
                      <span
                        v-else-if="
                          change.ai_augmentation.augmentation_variant ===
                          AugmentationVariantEnum.CHATBOT
                        "
                        class="text-xs font-medium"
                      >
                        Chatbot
                      </span>
                      <span
                        v-else-if="
                          change.ai_augmentation.augmentation_variant ===
                          AugmentationVariantEnum.EMAIL_EXTRACTION
                        "
                        class="text-xs font-medium"
                      >
                        E-Mail
                      </span>
                      <span
                        v-else-if="
                          change.ai_augmentation.augmentation_variant ===
                          AugmentationVariantEnum.CONTEXTUAL_ANALYSIS
                        "
                        class="text-xs font-medium"
                      >
                        Kontextanalyse
                      </span>
                      <span
                        v-else-if="
                          change.ai_augmentation.augmentation_variant ===
                          AugmentationVariantEnum.CALL
                        "
                        class="text-xs font-medium"
                      >
                        Phonebot
                      </span>
                    </div>
                    <div
                      v-else
                      class="flex items-center gap-1 whitespace-nowrap"
                    >
                      <Pencil class="h-4 w-4" />
                      <span class="text-xs font-medium">Manuelle Änderung</span>
                    </div>
                  </div>
                  <div class="text-right text-xs text-muted-foreground">
                    {{
                      new Date(change.created_at).toLocaleDateString('de-DE', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })
                    }}
                  </div>
                </div>
              </div>
            </RadioGroup>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  </div>
</template>
